/* eslint-disable no-plusplus */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Loading from '../../components/Loading';
import PaginationBar from '../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../components/SectionMeta';
import { listPermissions } from '../../services/users/permissionsApi';
import { notifyApiError } from '../../utils/feedback';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageTitle,
   PageSubtitle,
   PageHeaderText,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionHeader,
   SectionTitle,
   SectionBody,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar } from '../../styles/layout/ActionBar';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';

export default function Permissions() {
   const navigate = useNavigate();

   const [permissions, setPermissions] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   // Filtros
   const [permId, setPermId] = useState('');
   const [module, setModule] = useState('');
   const [resource, setResource] = useState('');
   const [action, setAction] = useState('');
   const [key, setKey] = useState('');
   const [label, setLabel] = useState('');
   const [submodule, setSubmodule] = useState('');

   const [currentPage, setCurrentPage] = useState(1);
   const itemsPerPage = 15;

   // Estado para ordenação
   const [sortConfig, setSortConfig] = useState({
      key: null,
      direction: null,
   });

   useEffect(() => {
      // eslint-disable-next-line no-use-before-define
      handleGetPermissions();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleClearFilters = () => {
      setPermId('');
      setModule('');
      setResource('');
      setAction('');
      setKey('');
      setLabel('');
      setSubmodule('');
      setPermissions([]);
      setCurrentPage(1);
      setSortConfig({ key: null, direction: null });
   };

   // FC1 desde 2026-09-15 (0111): o service lê `data.permissions` do envelope
   // JSend e traduz os filtros do contrato §3.1. `id` NÃO é filtro do backend
   // — resolve-se client-side sobre o resultado (o catálogo é fechado por
   // código, ~120 chaves; nada se perde).
   const handleGetPermissions = async (e) => {
      if (e) e.preventDefault();

      setIsLoading(true);
      try {
         let list = await listPermissions({
            module,
            submodule,
            resource,
            action,
            key,
            label,
         });
         if (permId) {
            list = list.filter((perm) => String(perm.id) === permId.trim());
         }
         setPermissions(list);
         setCurrentPage(1);
         setSortConfig({ key: null, direction: null });
      } catch (err) {
         notifyApiError(err);
      } finally {
         setIsLoading(false);
      }
   };

   const handleRowDoubleClick = (id) => {
      navigate(`/permissions/${id}`);
   };

   // Função para ordenação
   const handleSort = (sortKey) => {
      setSortConfig((prevConfig) => {
         if (prevConfig.key !== sortKey) {
            return { key: sortKey, direction: 'asc' };
         }

         if (prevConfig.direction === 'asc') {
            return { key: sortKey, direction: 'desc' };
         }
         if (prevConfig.direction === 'desc') {
            return { key: null, direction: null };
         }
         return { key: sortKey, direction: 'asc' };
      });

      setCurrentPage(1);
   };

   const getSortIcon = (columnKey) => {
      if (sortConfig.key !== columnKey) {
         return ' ⇅';
      }

      if (sortConfig.direction === 'asc') {
         return ' ▲';
      }
      if (sortConfig.direction === 'desc') {
         return ' ▼';
      }
      return ' ⇅';
   };

   // Permissões ordenadas
   const sortedPermissions = useMemo(() => {
      if (!sortConfig.key || !sortConfig.direction) {
         return permissions;
      }

      const sorted = [...permissions].sort((a, b) => {
         const aValue = a[sortConfig.key];
         const bValue = b[sortConfig.key];

         if (aValue === null || aValue === undefined) return 1;
         if (bValue === null || bValue === undefined) return -1;

         if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'asc'
               ? aValue - bValue
               : bValue - aValue;
         }

         const aString = String(aValue).toLowerCase();
         const bString = String(bValue).toLowerCase();

         if (aString < bString) {
            return sortConfig.direction === 'asc' ? -1 : 1;
         }
         if (aString > bString) {
            return sortConfig.direction === 'asc' ? 1 : -1;
         }
         return 0;
      });

      return sorted;
   }, [permissions, sortConfig]);

   const totalPages = useMemo(() => Math.ceil(sortedPermissions.length / itemsPerPage), [sortedPermissions.length]);

   const currentPermissions = useMemo(() => {
      const indexOfLast = currentPage * itemsPerPage;
      const indexOfFirst = indexOfLast - itemsPerPage;
      return sortedPermissions.slice(indexOfFirst, indexOfLast);
   }, [sortedPermissions, currentPage]);

   const handlePageChange = (pageNumber) => {
      if (!totalPages) return;
      const next = Math.min(Math.max(1, pageNumber), totalPages);
      setCurrentPage(next);
   };

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Permissões</PageTitle>
               <PageSubtitle>
                  Visualize e gerencie as permissões do sistema. Dê duplo clique
                  na linha para ver detalhes.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         {/* Filtros */}
         <Section>
            <SectionHeader>
               <SectionTitle>Pesquisa</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <form onSubmit={handleGetPermissions}>
                  <FormGrid $cols={12} $colsMobile={12}>
                     <Field $col={2} $colMobile={6}>
                        <label>ID</label>
                        <input
                           type="text"
                           value={permId}
                           onChange={(e) => setPermId(e.target.value)}
                           placeholder="Ex.: 1"
                        />
                     </Field>

                     <Field $col={3} $colMobile={12}>
                        <label>Módulo</label>
                        <input
                           type="text"
                           value={module}
                           onChange={(e) => setModule(e.target.value)}
                           placeholder="Ex.: inventory"
                        />
                     </Field>

                     <Field $col={3} $colMobile={12}>
                        <label>Recurso</label>
                        <input
                           type="text"
                           value={resource}
                           onChange={(e) => setResource(e.target.value)}
                           placeholder="Ex.: movement"
                        />
                     </Field>

                     <Field $col={2} $colMobile={6}>
                        <label>Ação</label>
                        <input
                           type="text"
                           value={action}
                           onChange={(e) => setAction(e.target.value)}
                           placeholder="Ex.: read"
                        />
                     </Field>

                     <Field $col={2} $colMobile={6}>
                        <label>Submódulo</label>
                        <input
                           type="text"
                           value={submodule}
                           onChange={(e) => setSubmodule(e.target.value)}
                           placeholder="Ex.: dashboard"
                        />
                     </Field>

                     <Field $col={6} $colMobile={12}>
                        <label>Chave</label>
                        <input
                           type="text"
                           value={key}
                           onChange={(e) => setKey(e.target.value)}
                           placeholder="Ex.: inventory:movement:read"
                        />
                     </Field>

                     <Field $col={6} $colMobile={12}>
                        <label>Label</label>
                        <input
                           type="text"
                           value={label}
                           onChange={(e) => setLabel(e.target.value)}
                           placeholder="Ex.: Movimentos - visualizar"
                        />
                     </Field>

                     <Field $col={12} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <button
                              type="button"
                              onClick={handleClearFilters}
                              disabled={isLoading}
                           >
                              Limpar
                           </button>
                           <button type="submit" disabled={isLoading}>
                              Pesquisar
                           </button>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         {/* Resultados */}
         <Section>
            <SectionHeader>
               <SectionHeaderRow>
                  <SectionTitle>Resultados</SectionTitle>
                  <SectionMeta
                     items={[
                        {
                           key: 'count',
                           label: 'Total de permissões:',
                           value: permissions.length,
                        },
                        {
                           key: 'page',
                           label: 'Página',
                           value: `${currentPage}/${totalPages || 1}`,
                        },
                     ]}
                  />
               </SectionHeaderRow>
            </SectionHeader>

            <SectionBody>
               {permissions.length === 0 ? (
                  <EmptyState>
                     <h4>Sem resultados</h4>
                     <p>Use os filtros acima e clique em <strong>Pesquisar</strong>.</p>
                  </EmptyState>
               ) : (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th
                                    onClick={() => handleSort('id')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    ID{getSortIcon('id')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('module')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Módulo{getSortIcon('module')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('resource')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Recurso{getSortIcon('resource')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('action')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Ação{getSortIcon('action')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('key')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Chave{getSortIcon('key')}
                                 </th>
                                 <th
                                    onClick={() => handleSort('label')}
                                    style={{
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    title="Clique para ordenar"
                                 >
                                    Label{getSortIcon('label')}
                                 </th>
                              </tr>
                           </thead>

                           <tbody>
                              {currentPermissions.map((permission) => (
                                 <tr
                                    key={String(permission.id)}
                                    onDoubleClick={() =>
                                       handleRowDoubleClick(permission.id)
                                    }
                                    style={{ cursor: 'pointer' }}
                                    title="Duplo clique para ver detalhes"
                                 >
                                    <td>{permission.id}</td>
                                    <td>{permission.module}</td>
                                    <td>{permission.resource}</td>
                                    <td>{permission.action}</td>
                                    <td title={permission.key}>
                                       {permission.key}
                                    </td>
                                    <td>{permission.label || '-'}</td>
                                 </tr>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>
                     <div style={{ padding: '16, 0' }}>&nbsp;</div>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={permissions.length}
                        itemLabel="permissões"
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
